(() => {
  const SELECTOR = 'deferred-media[data-viewport-autoplay]';
  const ROOT_MARGIN = '300px 0px';

  if (window.SiripattuViewportVideoAutoplay) {
    window.SiripattuViewportVideoAutoplay.init(document);
    return;
  }

  const controllers = new WeakMap();

  const restorePoster = (media, video) => {
    if (video) {
      video.pause();
      video.remove();
    }

    media.removeAttribute('loaded');
  };

  const createController = (media) => {
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let observer;
    let video;
    let isNearViewport = false;
    let isAutoPausing = false;
    let isManuallyPaused = false;
    let autoplayFailed = false;

    const handlePlay = () => {
      isManuallyPaused = false;

      if (!isNearViewport) {
        isAutoPausing = true;
        video.pause();
        isAutoPausing = false;
      }
    };

    const handlePause = () => {
      if (!isAutoPausing && isNearViewport && !video.ended) {
        isManuallyPaused = true;
      }
    };

    const attachVideo = () => {
      const currentVideo = media.querySelector(':scope > video');
      if (!currentVideo || currentVideo === video) return currentVideo;

      if (video) {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
      }

      video = currentVideo;
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);

      return video;
    };

    const play = async () => {
      if (motionQuery.matches || autoplayFailed || isManuallyPaused) return;

      if (!media.hasAttribute('loaded')) {
        media.loadContent(false);
      }

      const currentVideo = attachVideo();
      if (!currentVideo) return;

      try {
        await currentVideo.play();
      } catch (error) {
        autoplayFailed = true;
        restorePoster(media, currentVideo);
        video = undefined;
        observer?.disconnect();
      }
    };

    const pause = () => {
      const currentVideo = attachVideo();
      if (!currentVideo || currentVideo.paused) return;

      isAutoPausing = true;
      currentVideo.pause();
      isAutoPausing = false;
    };

    const observe = () => {
      if (motionQuery.matches || observer || autoplayFailed) return;

      observer = new IntersectionObserver(
        ([entry]) => {
          isNearViewport = entry.isIntersecting;

          if (isNearViewport) {
            play();
          } else {
            pause();
          }
        },
        { rootMargin: ROOT_MARGIN, threshold: 0.01 }
      );
      observer.observe(media);
    };

    const handleMotionChange = () => {
      if (motionQuery.matches) {
        observer?.disconnect();
        observer = undefined;
        isNearViewport = false;
        restorePoster(media, attachVideo());
        video = undefined;
        isManuallyPaused = false;
        autoplayFailed = false;
      } else {
        observe();
      }
    };

    const destroy = () => {
      observer?.disconnect();
      motionQuery.removeEventListener('change', handleMotionChange);

      if (video) {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
      }
    };

    motionQuery.addEventListener('change', handleMotionChange);
    observe();

    return { destroy };
  };

  const init = (root = document) => {
    const mediaElements = root.matches?.(SELECTOR) ? [root] : root.querySelectorAll(SELECTOR);

    mediaElements.forEach((media) => {
      if (!controllers.has(media)) {
        controllers.set(media, createController(media));
      }
    });
  };

  const destroy = (root) => {
    const mediaElements = root.matches?.(SELECTOR) ? [root] : root.querySelectorAll(SELECTOR);

    mediaElements.forEach((media) => {
      controllers.get(media)?.destroy();
      controllers.delete(media);
    });
  };

  window.SiripattuViewportVideoAutoplay = { init, destroy };

  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
